import { useState, useMemo } from 'react';
import { api } from '@halo/web-lib';
import { Loader2 } from 'lucide-react';
import type { StoreListItem } from './types';

export function BackfillForm({
  storeId,
  store,
  onTriggered,
}: {
  storeId: number;
  store: StoreListItem | null;
  onTriggered: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('');
  const [months, setMonths] = useState(3);
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<string[] | null>(null);

  const PLATFORMS = ['shopify', 'meta', 'google_ads', 'tiktok_business', 'tiktok_shop', 'pinterest', 'klaviyo', 'ga4', 'amazon_seller', 'amazon_ads'];
  const connectedSet = new Set(store?.integrations ?? []);
  const platformOptions = useMemo(() => {
    const connected = PLATFORMS.filter((p) => connectedSet.has(p));
    const rest = PLATFORMS.filter((p) => !connectedSet.has(p));
    return [...connected, ...rest];
  }, [store]);

  function closeModal() {
    setOpen(false);
    setPlatform('');
    setMonths(3);
    setForce(false);
    setError(null);
    setSkipped(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSkipped(null);
    if (!platform) { setError('Select a platform'); return; }
    if (months < 1 || months > 24) { setError('Months must be 1–24'); return; }

    const days = months * 30;
    setSubmitting(true);
    const res = await api.post<{
      jobCount: number;
      skippedMonths?: string[];
      message?: string;
    }>('/api/v1/ingestion/admin/backfill', { storeId, platform, days, ...(force ? { force: true } : {}) });
    setSubmitting(false);

    if (res.success) {
      const data = res.data;
      if (data?.skippedMonths && data.skippedMonths.length > 0) {
        setSkipped(data.skippedMonths);
      }
      if (data?.jobCount === 0) {
        setError(data.message ?? 'All months already verified. Enable "Force" to re-backfill.');
        return;
      }
      closeModal();
      onTriggered();
    } else {
      setError(res.error ?? 'Failed to trigger backfill');
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors"
      >
        Trigger Backfill
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={closeModal}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6 w-full max-w-sm"
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Backfill — {store?.name ?? `Store #${storeId}`}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select platform...</option>
              {platformOptions.map((p) => (
                <option key={p} value={p}>{p}{connectedSet.has(p) ? ' ✓' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Months</label>
            <div className="flex gap-2">
              {[3, 6, 12].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setMonths(n)}
                  className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors ${
                    months === n
                      ? 'border-gray-900 bg-gray-900 dark:bg-gray-950 text-white'
                      : 'border-gray-200 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {n} mo
                </button>
              ))}
            </div>
            <input
              type="range"
              min="1"
              max="24"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value, 10))}
              className="w-full mt-2"
            />
            <div className="text-xs text-gray-400 dark:text-gray-500 text-center mt-0.5">{months} month{months !== 1 ? 's' : ''}</div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-gray-900"
            />
            <span className="text-xs text-gray-600 dark:text-gray-300">Force re-backfill verified months</span>
          </label>
        </div>
        {skipped && skipped.length > 0 && (
          <p className="text-xs text-amber-600 mt-3">
            Skipped {skipped.length} completed month{skipped.length !== 1 ? 's' : ''}: {skipped.join(', ')}
          </p>
        )}
        {error && <p className="text-xs text-red-500 mt-3">{error}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={closeModal} className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !platform}
            className="flex-1 px-3 py-2 text-sm bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Start
          </button>
        </div>
      </form>
    </div>
  );
}
